// src/components/richText/richTextPipeline.js
//
// The PURE half of <RichText> (authoring-5b-markdown): everything that decides what a
// body string MEANS, with no React and no DOM, so it runs under `node --test`.
//
// The stack is the one the repo already ships for AI answers (react-markdown +
// remark-gfm; see ChatMarkdown.jsx). NO new dependency, NO rehype-raw, NO raw-HTML
// path, no dangerouslySetInnerHTML -- react-markdown builds a React tree.
//
// WHY THIS IS NOT JUST `<ReactMarkdown>{body}</ReactMarkdown>`: the body text was
// written as PLAIN TEXT, by a non-technical author, before markdown existed here. The
// Step-0 audit (plans/authoring-5b-step0-audit.md) and the signed-in live scan found
// what that means in practice, and each piece below answers one finding:
//
//   M1  DISABLED_CONSTRUCTS  constructs no prose author intends -- an indented paragraph
//                            becoming a code box, `---` under a paragraph turning the
//                            whole paragraph into a heading, a `[1]: url` line vanishing,
//                            raw HTML, remote images, footnotes with colliding ids.
//   M2  remarkFaithfulNumbers  9 of the 16 numbered lists in live content start at
//                            2/3/4/6/7 (exam question numbers). <ol> renumbers from 1
//                            unless told otherwise; this pins every item to the numeral
//                            that was typed.
//   M3  preEscape            literal idioms markdown would EAT. The one proven on live
//                            data: two fill-in blanks in a sentence ("because ___, but
//                            ... because ___") pair up as bold-italic and swallow both
//                            blanks and the words between them.
//   M4  GFM_OPTIONS          only ~~double~~ tildes strike through.
//
// Guiding rule: NOTHING THE AUTHOR TYPED EVER DISAPPEARS, and nothing becomes a box or
// a heading by accident.

/* ------------------------------------------------------------------ M4 ---- */
export const GFM_OPTIONS = { singleTilde: false };

/* ------------------------------------------------------------------ M1 ---- */
// micromark construct names (verified against the installed micromark-core-commonmark
// and micromark-extension-gfm-footnote). Still ON: ATX `#` headings, lists, quotes,
// bold, italic, strikethrough, tables, links, autolinks, fenced code, thematic breaks.
export const DISABLED_CONSTRUCTS = [
    'codeIndented',      // 4 spaces / a tab -> <pre><code> box
    'setextUnderline',   // text + `---` underneath -> the PARAGRAPH becomes an <h2>
    'htmlFlow',          // raw HTML blocks
    'htmlText',          // raw inline HTML
    'definition',        // `[1]: https://...` -> the line silently vanishes
    'labelStartImage',   // `![x](url)` -> a live remote <img>
    'gfmFootnoteDefinition', 'gfmFootnoteCall', 'gfmPotentialFootnoteCall', // fixed ids collide across instances
];

export function remarkDisableConstructs() {
    const data = this.data();
    (data.micromarkExtensions || (data.micromarkExtensions = [])).push({ disable: { null: DISABLED_CONSTRUCTS } });
}

/* --------------------------------------------------------- tree walking ---- */
// A ten-line walker instead of unist-util-visit: that package is only a TRANSITIVE
// dependency here, and importing an undeclared dependency is a new dependency in all
// but name.
function walk(node, fn, parent = null) {
    fn(node, parent);
    if (node && Array.isArray(node.children)) {
        // Copy: visitors below replace `children` wholesale.
        for (const child of [...node.children]) walk(child, fn, node);
    }
}

/* ------------------------------------------------------------------ M2 ---- */
// <li value=N> from the numeral the author typed. `start` on the <ol> covers a list
// that begins at 6; `value` also covers one that SKIPS (1, 2, 4), which <ol> would
// otherwise show as 1, 2, 3 -- a silent change to exam question numbers.
export function remarkFaithfulNumbers() {
    return (tree, file) => {
        const src = String(file);
        walk(tree, (node) => {
            if (node.type !== 'list' || !node.ordered) return;
            for (const li of node.children) {
                const at = li.position && li.position.start && li.position.start.offset;
                const m = typeof at === 'number' ? /^(\d{1,9})[.)]/.exec(src.slice(at, at + 12)) : null;
                if (m) {
                    li.data = li.data || {};
                    li.data.hProperties = { ...(li.data.hProperties || {}), value: Number(m[1]) };
                }
            }
        });
    };
}

/* ------------------------------------------------- unmarked text fidelity ---- */
// Today every body is ONE <p class="whitespace-pre-wrap">: a blank line is an empty
// line box, N blank lines are N of them, and runs of spaces survive. RichText keeps
// `white-space: pre-wrap` on its text blocks (see RichText.jsx / lesson.css), and this
// plugin supplies the two things pre-wrap alone cannot:
//
//   - the GAP between blocks. Markdown collapses any number of blank lines into one
//     block boundary; the count is recovered from the source positions and handed to
//     CSS as --rt-gap, where it becomes N x line-height -- exactly the empty line
//     boxes the old <p> drew.
//   - hard breaks. A `break` node becomes <br> FOLLOWED BY a newline text node, which
//     under pre-wrap is two line breaks. It is turned back into the newline it was.
export function remarkPreserveSpacing() {
    return (tree) => {
        walk(tree, (node) => {
            if (!Array.isArray(node.children)) return;
            if (node.type === 'root' || node.type === 'blockquote' || node.type === 'listItem' || node.type === 'list') {
                let prev = null;
                for (const child of node.children) {
                    if (prev && prev.position && child.position) {
                        // 0 is a real answer: "Remember:\n- one" has NO blank line, and
                        // the old <p> drew none.
                        const gap = Math.max(0, child.position.start.line - prev.position.end.line - 1);
                        child.data = child.data || {};
                        child.data.hProperties = { ...(child.data.hProperties || {}), style: '--rt-gap:' + gap };
                    }
                    prev = child;
                }
            }
            if (node.children.some((c) => c.type === 'break')) {
                node.children = node.children.map((c) => (c.type === 'break' ? { type: 'text', value: '\n' } : c));
            }
        });
    };
}

// mdast-util-to-hast puts a "\n" text node between block elements. Inside a pre-wrap
// container each of those would be drawn as an empty line. They carry no content, so
// they are dropped -- but ONLY between blocks: a "\n" inside a paragraph is the
// author's line break and must stay.
const BLOCK_PARENTS = new Set(['ul', 'ol', 'blockquote', 'table', 'thead', 'tbody', 'tr']);
const BLOCK_TAGS = new Set(['p', 'ul', 'ol', 'blockquote', 'pre', 'table', 'hr', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'div']);
export function rehypeDropBlockWhitespace() {
    return (tree) => {
        walk(tree, (node) => {
            if (!Array.isArray(node.children)) return;
            const isEl = (c) => c && c.type === 'element';
            const between = node.type === 'root'
                || (isEl(node) && BLOCK_PARENTS.has(node.tagName))
                || (isEl(node) && node.tagName === 'li' && node.children.some((c) => isEl(c) && BLOCK_TAGS.has(c.tagName)));
            if (!between) return;
            node.children = node.children.filter((c) => !(c.type === 'text' && /^\s*$/.test(c.value || '')));
        });
    };
}

/* ------------------------------------------------------------ ==highlight== ---- */
// `==like this==` -> <mark>. Works across inline formatting (`==a **b** c==`) because
// the delimiters are matched over a parent's DIRECT text children and everything
// between them is wrapped. An opener must be followed by a non-space and a closer
// preceded by one, so prose such as "a == b" is left alone. Never inside code: inline
// code and code blocks are not `text` nodes.
const HL = /==/g;
function splitHighlights(children) {
    // 1. Explode text nodes into text / delimiter tokens.
    const tokens = [];
    for (const child of children) {
        if (child.type !== 'text' || !child.value.includes('==')) { tokens.push({ node: child }); continue; }
        const v = child.value;
        let last = 0;
        let m;
        HL.lastIndex = 0;
        while ((m = HL.exec(v)) !== null) {
            // A run of 3+ `=` is a separator someone typed, not a delimiter.
            if (v[m.index - 1] === '=' || v[m.index + 2] === '=') continue;
            const before = v[m.index - 1];
            const after = v[m.index + 2];
            tokens.push({ node: { type: 'text', value: v.slice(last, m.index) } });
            tokens.push({
                delim: true,
                canOpen: after !== undefined && !/\s/.test(after),
                canClose: before !== undefined && !/\s/.test(before),
            });
            last = m.index + 2;
        }
        tokens.push({ node: { type: 'text', value: v.slice(last) } });
    }
    if (!tokens.some((t) => t.delim)) return null;
    // 2. Pair them up, left to right; an unpaired delimiter goes back to literal text.
    const out = [];
    let open = null; // { at: index in out }
    for (const t of tokens) {
        if (!t.delim) { if (t.node.type !== 'text' || t.node.value) out.push(t.node); continue; }
        if (open === null && t.canOpen) { open = { at: out.length }; out.push({ type: 'text', value: '==' }); continue; }
        if (open !== null && t.canClose && out.length > open.at + 1) {
            const inner = out.splice(open.at + 1);
            out[open.at] = { type: 'mark', data: { hName: 'mark' }, children: inner };
            open = null;
            continue;
        }
        out.push({ type: 'text', value: '==' });
    }
    // 3. Re-join neighbouring text so a literal "==" does not fragment the paragraph.
    const merged = [];
    for (const n of out) {
        const prev = merged[merged.length - 1];
        if (n.type === 'text' && prev && prev.type === 'text') prev.value += n.value;
        else merged.push(n);
    }
    return merged;
}
export function remarkHighlight() {
    return (tree) => {
        walk(tree, (node) => {
            if (!Array.isArray(node.children) || node.type === 'mark') return;
            if (!node.children.some((c) => c.type === 'text' && c.value.includes('=='))) return;
            const next = splitHighlights(node.children);
            if (next) node.children = next;
        });
    };
}

/* ------------------------------------------------- glossary composition ---- */
// AnnotatedText's job, done on markdown TEXT NODES instead of the raw string -- so a
// term inside **bold** is still found, and markdown syntax is never mistaken for prose.
//
//   - ONE `seen` set per body: AnnotatedText marks the FIRST occurrence of a term per
//     call, and a body is now many text nodes. Without the shared set a term would be
//     marked once per fragment (once in every list item, every bold run).
//   - never inside a link (a popover trigger nested in an <a> is a button inside a
//     link) and never inside code -- which needs no check: inline code and code blocks
//     are not `text` nodes, so the walker never sees their content.
//   - a term that SPANS a markdown boundary ("dramatic **irony**") is two text nodes
//     and simply does not match. Nothing breaks; it is left as prose.
//
// The term is emitted as a <gloss-term data-term="..."> element; RichText.jsx maps it
// to the same GlossTerm popover AnnotatedText uses.
export function remarkGlossary(matcher) {
    return () => (tree) => {
        if (!matcher || !matcher.regex || !matcher.byLower) return;
        const seen = new Set();
        const visit = (node, insideLink) => {
            if (!Array.isArray(node.children)) return;
            const linked = insideLink || node.type === 'link' || node.type === 'linkReference';
            const next = [];
            for (const child of node.children) {
                if (child.type !== 'text' || linked || !child.value) { next.push(child); visit(child, linked); continue; }
                const v = child.value;
                const re = matcher.regex;
                re.lastIndex = 0;
                let last = 0;
                let m;
                let hit = false;
                while ((m = re.exec(v)) !== null) {
                    if (!m[0]) { re.lastIndex += 1; continue; }
                    const lower = m[0].toLowerCase();
                    if (!matcher.byLower.has(lower) || seen.has(lower)) continue;
                    seen.add(lower);
                    hit = true;
                    if (m.index > last) next.push({ type: 'text', value: v.slice(last, m.index) });
                    next.push({
                        type: 'glossTerm',
                        data: { hName: 'gloss-term', hProperties: { dataTerm: lower } },
                        children: [{ type: 'text', value: m[0] }],
                    });
                    last = m.index + m[0].length;
                }
                re.lastIndex = 0;
                if (!hit) { next.push(child); continue; }
                if (last < v.length) next.push({ type: 'text', value: v.slice(last) });
            }
            node.children = next;
        };
        visit(tree, false);
    };
}

/* ------------------------------------------------------------------ M3 ---- */
// Runs on the string BEFORE parsing. Every rule makes a literal idiom stay literal; none
// of them can remove a character. Code (fenced blocks and `inline` spans) is skipped,
// because a backslash inside code is shown as a backslash.
//
//   (a) a run of 3+ underscores is a BLANK, never emphasis. Proven on live content:
//       "...clear because ___, but it is not yet securely effective because ___." The
//       two runs pair into bold-italic and swallow both blanks and the words between.
//       `_italic_` and `__bold__` (1-2 underscores) are untouched.
//   (b) "A*" keeps its star: "*Note: an A* answer" otherwise italicises "Note: an A".
//   (c) a block that uses `+ ` line starts is a plus/minus evaluation list; as bullets
//       both signs would become the same dot. BOTH stay literal, in that block only --
//       an ordinary `- ` list elsewhere in the same body is still a list.
const CODE_SPLIT = /(^ {0,3}(`{3,}|~{3,})[^\n]*\n[\s\S]*?(?:^ {0,3}\2[^\n]*$|$(?![\s\S]))|`+[^`\n]*`+)/m;
function escapeProse(s) {
    return s.split(/(\n[ \t]*\n)/).map((block) => {
        if (/^\n[ \t]*\n$/.test(block)) return block;
        let b = block
            .replace(/_{3,}/g, (run) => run.replace(/_/g, '\\_'))
            .replace(/\bA\*(?!\*)/g, 'A\\*');
        if (/^ {0,3}\+(?=\s)/m.test(b)) b = b.replace(/^( {0,3})([+-])(?=\s)/gm, '$1\\$2');
        return b;
    }).join('');
}
export function preEscape(input) {
    const src = typeof input === 'string' ? input : '';
    if (!src) return '';
    // split() with a capturing regex that has an inner group yields [text, code, fence, text, ...]
    const parts = src.split(CODE_SPLIT);
    let out = '';
    for (let i = 0; i < parts.length; i += 3) {
        out += escapeProse(parts[i] || '');
        if (parts[i + 1] !== undefined) out += parts[i + 1];
    }
    return out;
}

/* ------------------------------------------------------------ safety valve ---- */
// Found by the adversarial pass: a line nested ~10,000 block quotes deep ("> > > ...")
// overflows the stack inside remark-gfm's own (recursive) tree walker -- a third-party
// RangeError thrown during render. Nobody writes that on purpose, but a lesson must not
// go blank because of a pasted string. Anything nested deeper than a person would type
// is rendered as plain text instead (RichText.jsx also wraps the renderer in an error
// boundary, so an unforeseen parser failure degrades the same way).
export const MAX_QUOTE_DEPTH = 12;
export function isTooDeep(input) {
    const src = typeof input === 'string' ? input : '';
    // Any container marker counts: "> > >", "- - -" and "1. 1. 1." all nest one level each.
    const marker = /^(?:[ \t]*(?:>|[-*+](?=[ \t])|\d{1,9}[.)](?=[ \t])))+/gm;
    let m;
    while ((m = marker.exec(src)) !== null) {
        const depth = (m[0].match(/>|[-*+](?=[ \t]|$)|\d{1,9}[.)]/g) || []).length;
        if (depth > MAX_QUOTE_DEPTH) return true;
        if (!m[0]) marker.lastIndex += 1;
    }
    return false;
}

/* ------------------------------------------------------- plugin bundles ---- */
// The ONE place the pipeline is assembled, so the component, the tests and any future
// caller cannot drift apart. `remarkGfm` is injected (this file imports nothing).
export function remarkPluginsFor(remarkGfm, matcher) {
    return [
        [remarkGfm, GFM_OPTIONS],
        remarkDisableConstructs,
        remarkFaithfulNumbers,
        remarkPreserveSpacing,
        remarkHighlight,
        ...(matcher ? [remarkGlossary(matcher)] : []),
    ];
}
export const REHYPE_PLUGINS = [rehypeDropBlockWhitespace];

// Links: http(s) and mailto only. Anything else (javascript:, data:, relative) is shown
// as its text. react-markdown's own urlTransform already blanks javascript:; this is
// the second lock, and it is the rule ChatMarkdown.jsx uses.
export const SAFE_LINK = /^(https?:|mailto:)/i;

/* -------------------------------------------------------- prerender strip ---- */
// scripts/prerender.mjs bakes free lessons into static HTML as escaped TEXT. Once a
// body uses markdown a crawler would read literal `**`. This removes the MARKERS and
// keeps every word: a plain-text rendering, not a parser. List markers and numerals
// are kept on purpose -- "1. Plan" reads correctly as text.
export function stripMarkdown(input) {
    let s = typeof input === 'string' ? input : '';
    if (!s) return '';
    s = s.replace(/^ {0,3}#{1,6}[ \t]+/gm, '');
    s = s.replace(/^ {0,3}>[ \t]?/gm, '');
    s = s.replace(/\[([^\]\n]+)\]\((?:[^()\s]|\([^()\s]*\))+\)/g, '$1');
    s = s.replace(/(\*\*|~~|==)(?=\S)([^\n]*?\S)\1/g, '$2');
    s = s.replace(/(^|[^\w*])\*(?=[^\s*])([^*\n]*?[^\s*])\*(?![\w*])/g, '$1$2');
    s = s.replace(/(^|[^\w_])_(?=[^\s_])([^_\n]*?[^\s_])_(?![\w_])/g, '$1$2');
    return s;
}
